import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import { PlayCircleOutlined, EditOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { Button, Input, message, Popconfirm, Table, Tag } from 'antd';
import axiosInstance from '../../utils/axios';
import { dateFormat } from '../../utils/dateFormats';
import { useNavigate } from 'react-router-dom';

const Archived = () => {
	const navigate = useNavigate();
	const [movies, setMovies] = useState({
		results: []
	});
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);

	useEffect(() => {
		const delayFetch = debounce(() => {
			fetchData();
		}, 550); // 550ms debounce

		delayFetch();
		
		return () => delayFetch.cancel();

		// eslint-disable-next-line
	}, [search, page]);

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(1);
	}
	
	const fetchData = async () => {
		setLoading(true);
		try {
			const response = await axiosInstance.get(`movie/movie/get_archived_movies/?page=${page}&search=${search}`);

			setMovies(response.data);
		} catch (error) {
			message.error("Failed to fetch movie");
		} finally {
			setLoading(false);
		}
	};

	const cancel = () => message.info('Cancelled');

	const confirm = async (id) => {
		try {
			await axiosInstance.delete(`movie/movie/${id}/`);
			message.success("Deleted successfully");
			setPage(1);
			fetchData();
		} catch (err) {
			message.error("Cannot delete");
		}
	};

	const changeStatus = async (id) => {
		setLoading(true);
    try {
      await axiosInstance.patch(`movie/movie/${id}/`, {status: 'coming_soon'});
      navigate('/admin/movie/upcoming');
    } catch (error) {
      message.error("Cannot change status");
    } finally {
			setLoading(false);
		}
  }

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Directed by',
			dataIndex: 'directed_by',
			key: 'directed_by',
		},
		{
			title: 'Genre',
			dataIndex: 'genre',
			key: 'genre',
			render: (_, record) => (
				<>
					{
						record.genre && record.genre.map((item) => <Tag key={item.id}>{item.name}</Tag>)
					}
				</>
			)
		},
		{
			title: 'Language',
			dataIndex: 'language',
			key: 'language',
			render: (_, record) => (
				<>{ record.language && record.language.name}</>
			)
		},
		{
			title: 'Created at',
			dataIndex: 'created_at',
			key: 'created_at',
			render: text => dateFormat(text),
		},
		{
			title: 'Action',
			dataIndex: '',
			key: 'actions',
			render: (_, record) => (
				<>
					<Popconfirm
						title="Archive to Upcoming"
						description="Are you sure to change status to coming soon?"
						onConfirm={() => changeStatus(record.id)}
						onCancel={cancel}
						okText="Yes"
						cancelText="No"
					>
						<Button type="link" icon={<EditOutlined />} />
					</Popconfirm>
					<Popconfirm
						title="Delete"
						description="Are you sure to delete this item?"
						onConfirm={() => confirm(record.id)}
						onCancel={cancel}
						okText="Yes"
						cancelText="No"
					>
						<Button type="link" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</>
			)
		},
	];

  return (
		<div className='dashboard'>
			<PageHeading name="Archived Movies" icon={<PlayCircleOutlined/>} />
			<div style={{
				display: 'flex',
				flexWrap: 'wrap',
				gap: '12px',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: 16
			}}>
				<Input
					placeholder="Search here"
					prefix={<SearchOutlined />}
					style={{ width: "190px" }}
					onChange={(e) => handleSearch(e)}
				/>
				<Button type="primary" onClick={() => navigate("/admin/movie/add-new")}>Add new</Button>
			</div>

			<Table
				dataSource={movies.results}
				loading={loading}
				columns={columns}
				rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
					current: page,
					pageSize: 20,
					total: movies.count,
					onChange: (newPage, _) => {
						setPage(newPage);
					}
				}}
			/>
    </div>
  )
}

export default Archived